import React, { useCallback, useEffect, useState } from "react";
import { Dialog, Notification } from "@rio-cloud/rio-uikit";
import Button from "@rio-cloud/rio-uikit/Button";
import { DICT, forceHHMM, formatMaskTime } from "@utils";
import { CeckpointStepV2, CehckpointFieldsV2, CehckpointFieldV2, CheckpointV2 } from "src/types/checkpoint";
import { StepRender } from "./StepRender";
import ConfirmDialog from "../Dialogs/Confirm";
import { getTemplateCheckpointV2, getTemplatesCheckpointV2, updateCheckpointV2 } from "src/services/checkpoint";
import { hhmmToMinutes, minutesToHHMM, time2minutes } from "src/utils/time2minutes";

interface Props {
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  checkpoint: CheckpointV2;
}

const EditCheckpoint = ({ open, onClose, checkpoint }: Props) => {
  const [checkpointType, setCheckpointType] = useState("");

  const [title, setTitle] = useState("");
  const [estimatedTime, setEstimatedTime] = useState("");

  const [quantity, setQuantity] = useState(0);
  const [steps, setSteps] = useState<CeckpointStepV2[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [templates, setTemplates] = useState<CeckpointStepV2[]>([]);
  const [menu, setMenu] = useState<string[]>([]);

  const init = useCallback(async () => {
    const response = await getTemplatesCheckpointV2();
    if (response.status === "success") {
      setTemplates(response.data.result);
      const itens = (response.data.result as CeckpointStepV2[])
        .map((s) => s.title)
        .filter((title): title is string => typeof title === "string");

      setMenu(itens);
    }

    // const checkpointTypeRequest = checkpointType || checkpoint.checkpointType;
    // try {
    //   const response = await getTemplateCheckpointV2(checkpointTypeRequest);
    //   if (response.data.result) {
    //     const templates = response.data.result as CeckpointStepV2[];
    //     setTemplate(templates);
    //     const stepsWithFieldData = steps.map((step) => ({
    //       ...step,
    //       fields: step.fields?.map((fi) => {
    //         // Procura o campo correspondente no template pelo id
    //         const templateField = templates
    //           .flatMap((s) => s.fields || [])
    //           .find((f) => {
    //             return String(f.field?.id) === String(fi.field?.id);
    //           });

    //         return {
    //           ...fi,
    //           field: {
    //             ...fi.field,
    //             name: templateField?.field?.name ?? fi.field?.name,
    //             options: templateField?.field?.options ?? fi.field?.options,
    //           },
    //         };
    //       }),
    //     }));
    //     setSteps(stepsWithFieldData);
    //   }
    // } catch (error) {
    //   console.error("Erro ao buscar template:", error);
    // }
  }, []);

  useEffect(() => {
    if (open) {
      init();
    }
  }, [open, init]);

  useEffect(() => {
    const totalMinutes = steps.reduce((total, step) => {
      return total + time2minutes(step.estimatedTimeMinutes ?? "00:00");
    }, 0);

    const totalTime = minutesToHHMM(totalMinutes);
    setEstimatedTime(totalTime);
  }, [steps]);

  const handleDeleteStep = (i: number) => {
    setSteps((prevSteps) => prevSteps.filter((_, index) => index !== i));
  };

  const handleChangeCheckpointType = (checkpointType: string) => {
    setCheckpointType(checkpointType);
    setShowConfirmDialog(true);
  };

  const handleConfirmChangeCheckpointType = async () => {
    const selected = templates.filter((m) => m.title === checkpointType);
    if (selected) {
      setSteps(selected);
    }
    setShowConfirmDialog(false);
  };

  const fieldMap = (fields:CeckpointStepV2[], field?: CehckpointFieldV2) => {
    return fields?.flatMap((t) => t.fields || []).find((f) => f.field?.id === field?.id)?.field
  }

  const handleSubmit = async () => {
    const key = Object.keys(DICT).find((k) => DICT[k as keyof typeof DICT] === checkpointType);

    const payload: any = {
      checkpointType: key,
      title,
      estimatedTimeMinutes: time2minutes(estimatedTime),
      quantity,
      steps: steps.map(({ estimatedTimeMinutes, ...step }) => ({
        ...step,
        estimatedTimeMinutes: time2minutes(estimatedTimeMinutes ?? ""),
        fields: step.fields?.map(({ field, ...rest }) => ({
          ...rest,
          field: fieldMap(templates, field),
        })),
      })),
    };

    try {
      const response = await updateCheckpointV2(checkpoint.id, payload);

      if (response.status === "success") {
        Notification.success("Checklist de parada criado com sucesso.");

        handleReset();
        return;
      }

      Notification.error("Erro ao criar checklist");
    } catch (error) {
      console.error("Erro ao criar checklist:", error);
      Notification.error("Erro ao criar checklist");
      return;
    }
  };

  const handleReset = () => {
    onClose(false);
  };

  const addStep = async () => {
    const key = Object.keys(DICT).find((k) => DICT[k as keyof typeof DICT] === checkpointType);

    try {
      const response = await getTemplateCheckpointV2(key!);
      if (response.status === "success") {
        setSteps((prevSteps) => [...prevSteps, ...response.data.result]);
        return;
      }
      Notification.error("Erro ao adicionar etapa");
    } catch (error) {
      Notification.error("Erro ao adicionar etapa");
    }
  };

  useEffect(() => {
    if (checkpoint && open) {
      const checkpointTypeValue = DICT[checkpoint.checkpointType as keyof typeof DICT] || checkpoint.checkpointType;
      setCheckpointType(checkpointTypeValue);
      setTitle(checkpoint.title);
      setEstimatedTime(minutesToHHMM(checkpoint.estimatedTimeMinutes));
      setQuantity(checkpoint.quantity);
      setSteps(checkpoint.steps || []);
    }
  }, [checkpoint, open]);

  return (
    <>
      <Dialog
        show={open}
        onClose={() => handleReset()}
        onEsc={() => handleReset()}
        bsSize="xl"
        className="checkpointDialog"
        bodyClassName="padding-25"
        title="Selecionar/editar Checklist de parada"
        body={
          <div>
            <div className="lead">
              <div className="text-size-h6 text-bold">Dados do checklist</div>

              <div className="display-flex flex-column gap-24">
                <div className="display-flex flex-row gap-10">
                  <div className="flex-1-1 form-group">
                    <label form="checkpoint_type">Tipo de parada</label>
                    <select
                      disabled
                      className="form-control"
                      id="select"
                      value={checkpointType}
                      onChange={(e) => handleChangeCheckpointType(e.target.value)}
                    >
                      {menu.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1-1 form-group">
                    <label form="title">Título do checklist</label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="flex-1-1 form-group">
                    <label form="time">Tempo estimado do checklist</label>
                    <input
                      disabled
                      id="time"
                      type="text"
                      className="form-control"
                      min="00:00"
                      // max="23:59"
                      value={estimatedTime}
                      placeholder="00:00"
                    />
                  </div>
                  <div className="flex-1-1 form-group">
                    <label form="quantity">Quantidade (Unidades)</label>
                    <input
                      type="number"
                      className="form-control"
                      id="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />
            {steps.length > 0 ? (
              <StepRender steps={steps} deleteStep={handleDeleteStep} updateStep={setSteps} />
            ) : (
              <></>
            )}

            <Button iconName="rioglyph rioglyph-plus" onClick={addStep}>
              Adicionar Etapa
            </Button>
            <hr />
            <div className="display-flex justify-content-end">
              <Button className="btn btn-primary" onClick={handleSubmit}>
                Salvar Checklist
              </Button>
            </div>
          </div>
        }
      ></Dialog>
      <ConfirmDialog
        show={showConfirmDialog}
        onClickConfirm={async () => await handleConfirmChangeCheckpointType()}
        onClickCancel={() => {}}
        title="Alterar Tipo de checklist de parada"
        content="Você tem certeza que deseja alterar o tipo de checklist? Isso pode afetar as etapas associadas."
        confirmButtonText="Sim, alterar"
        cancelButtonText="Não, manter"
      />
    </>
  );
};

export default EditCheckpoint;
